/**
 * ConfluenceTab — User Story US-2: Searchable wiki of how-to articles.
 *
 * Three-pane layout:
 *   - Top: Search bar
 *   - Left: Category-grouped article list
 *   - Right: Selected article (markdown-rendered)
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import SearchBar from './tasks/SearchBar';
import ArticleList from './tasks/ArticleList';
import ArticleViewer from './tasks/ArticleViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfluenceTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Fetch all articles on mount
  const fetchArticles = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/documentation/articles`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
        // Auto-select first article (Getting Started)
        if (data.articles?.length > 0 && !selectedSlug) {
          setSelectedSlug(data.articles[0].slug);
        }
      }
    } catch (e) {
      console.error('Failed to fetch articles', e);
    } finally {
      setLoading(false);
    }
  }, [selectedSlug]);

  useEffect(() => {
    fetchArticles();
     
  }, []);

  // Search handler (debounced inside SearchBar)
  const handleSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/documentation/articles/search?q=${encodeURIComponent(q)}`,
        { credentials: 'include' },
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      }
    } catch (e) {
      console.error('Search failed', e);
    } finally {
      setSearching(false);
    }
  }, []);

  const displayed = searchResults !== null ? searchResults : articles;

  if (loading) {
    return (
      <Card className="p-12 flex items-center justify-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading wiki…
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="confluence-tab">
      {/* Search bar — always visible at top */}
      <SearchBar
        onSearch={handleSearch}
        searching={searching}
        resultCount={searchResults?.length}
        query={searchQuery}
      />

      {/* Two-pane: list left, article right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 xl:col-span-3">
          <ArticleList
            articles={displayed}
            selectedSlug={selectedSlug}
            onSelect={setSelectedSlug}
            isSearchResults={searchResults !== null}
          />
        </div>
        <div className="lg:col-span-8 xl:col-span-9">
          <ArticleViewer slug={selectedSlug} />
        </div>
      </div>
    </div>
  );
}
